import { Story } from 'inkjs';
import { Choice } from 'inkjs/engine/Choice';
import { useEffect, useRef, useState } from 'react';
import { GameInputsMediator } from '@game-libs/inputs-mediator';
import { EventsManager } from '@game-libs/events-manager';
import { Effect, Linebreaker, OnChar, Pace, useSkip, WindupChildren } from 'windups';
import { parseTags } from './helpers';
import { MoveFocusInside } from 'react-focus-lock';
import { DialogActions, GameContext, InputActions } from '@game-libs/models';
import { GlobalGameStore } from '@game-libs/global-game-store';

type DialogProps = {
  story: Story;
  storyId: string;
  speakerAssets: string;
  isDismissable: boolean;
  onFinished: () => void;
};

type Line = {
  text: string;
  pace: number;
  classes: string;
  event: string | undefined;
  events: string[] | undefined;
  wait: number;
  isSilent: boolean;
};

type DialogInfo = {
  event: string | undefined;
  events: string[] | undefined;
  wait: number;
  speaker: string;
  lines: Line[];
  choices: Choice[];
  linesWidth: number;
};

const im = GameInputsMediator();

export const Dialog = ({ story, onFinished, speakerAssets, isDismissable }: DialogProps) => {
  const [currentSentence, setCurrentSentence] = useState<DialogInfo>({
    event: undefined,
    events: undefined,
    wait: 30,
    speaker: '',
    lines: [],
    choices: [],
    linesWidth: 500,
  });
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const dialogTextRef = useRef<HTMLDivElement>(null);

  const { actions } = GlobalGameStore();
  const { emit } = EventsManager();

  useEffect(() => {
    actions.setContext(GameContext.DIALOG);

    const unbind = im.inputEvents.on(InputActions.OTHER1, () => {
      isDismissable && onFinished();
    });
    const unbind2 = im.inputEvents.on(InputActions.OPTIONS, () => {
      isDismissable && onFinished();
    });

    continueStory();

    return () => {
      unbind();
      unbind2();
    };
  }, []);

  const continueStory = () => {
    if (!story) {
      return;
    } else if (!story.canContinue && story.currentChoices.length === 0) {
      onFinished();
    } else if (story.canContinue) {
      setIsTyping(true);
      let chainNextLine = false;
      let skipTyping = false;
      const nextLines: DialogInfo = {
        wait: 30,
        event: undefined,
        events: undefined,
        speaker: '',
        lines: [],
        choices: [],
        linesWidth: 500,
      };

      do {
        let text = story.Continue() || '';
        const choices = story.currentChoices;

        let { pace, speaker, classes, event, events, wait, isSilent, ...rest } = parseTags(
          story?.currentTags,
          text,
          choices,
        );

        chainNextLine = rest.chainNextLine;
        skipTyping = rest.skipTyping || skipTyping;

        pace = skipTyping ? 0 : pace;

        if (speaker.length) {
          nextLines.speaker = speaker;
        }
        if (choices.length) {
          nextLines.choices.push(...choices);
        }
        nextLines.lines.push({ text, pace, classes, event, events, wait, isSilent });
      } while (story.canContinue && chainNextLine);

      document.body.style.setProperty('--speaker-background', `url(${speakerAssets}/${nextLines.speaker}.png)`);

      // Get from config
      const fontMultiplier = 0.8;
      const speakerContainer = 150;

      const containerWidth = +((dialogTextRef.current?.clientWidth || 99) * fontMultiplier).toFixed(0);

      setCurrentSentence(() => ({
        wait: nextLines.wait,
        event: nextLines.event,
        events: nextLines.events,
        speaker: nextLines.speaker,
        lines: [...nextLines.lines],
        choices: nextLines.choices,
        linesWidth: nextLines.speaker ? containerWidth - speakerContainer : containerWidth,
      }));
    }
  };

  const selectChoice = (index: number) => {
    story?.ChooseChoiceIndex(index);
    continueStory();
  };

  if (!story) {
    return null;
  }

  return (
    <div className="dialog-wrapper" data-testid="dialog-wrapper">
      <div className="dialog-text-wrapper" ref={dialogTextRef}>
        {currentSentence?.speaker && <div data-speaker-id={currentSentence.speaker} className="line-speaker"></div>}
        <div className="dialog-text">
          <Linebreaker fontStyle={'30px Arial'} width={currentSentence.linesWidth}>
            <WindupChildren>
              <SkipTyping />
              {currentSentence.lines.map((line) => (
                <OnChar
                  key={line.text}
                  fn={(char) => {
                    line.pace > 5 &&
                      char !== ' ' &&
                      !line.isSilent &&
                      emit(DialogActions.TYPING, currentSentence?.speaker);
                  }}
                >
                  {line.event && (
                    <Effect
                      fn={() => {
                        line.event && emit(line.event);
                      }}
                    />
                  )}
                  {line.events && line.events.length > 0 && (
                    <Effect
                      fn={() => {
                        line.events!.forEach((event) => {
                          emit(event);
                        });
                      }}
                    />
                  )}
                  <Pace ms={line.pace}>
                    <span className={line.classes}>{line.text.trim()}</span>
                  </Pace>
                  <Pace ms={line.wait}> </Pace>
                </OnChar>
              ))}
              <Effect
                fn={() => {
                  setTimeout(() => {
                    setIsTyping(() => false);
                  }, 300);
                }}
              />
            </WindupChildren>
          </Linebreaker>
        </div>
      </div>

      <div className="choice-prompts">
        {!isTyping &&
          currentSentence.choices.length > 0 &&
          currentSentence.choices.map((choice, index) => {
            if (index === 0) {
              return (
                <MoveFocusInside key={choice.text}>
                  <button className="choice-prompt button" onClick={() => selectChoice(choice.index)}>
                    {choice.text}
                  </button>
                </MoveFocusInside>
              );
            }
            return (
              <button key={choice.text} className="choice-prompt button" onClick={() => selectChoice(choice.index)}>
                {choice.text}
              </button>
            );
          })}

        {!isTyping && currentSentence.choices.length === 0 && (
          <MoveFocusInside>
            <button
              className="no-choice-prompt"
              onClick={() => {
                continueStory();
              }}
            >
              {''}
            </button>
          </MoveFocusInside>
        )}
      </div>
    </div>
  );
};

const SkipTyping = () => {
  const skip = useSkip();

  useEffect(() => {
    const unbind = im.inputEvents.on(InputActions.SELECT, () => {
      skip();
    });

    return () => {
      unbind();
    };
  }, []);

  return null;
};
